import { head } from 'lodash';

import { WebRequestService } from 'app/services/web_request_service';
import { Balance, Order, WalletMapping } from 'app/types/api';
import type { SettingsData } from 'app/pages/settings';
import { AuthData } from 'app/types/auth';
import { AddressInfo } from 'app/types/ethplorer';
import { rememberDebugUser } from 'app/utils/debug_errors';

const makeHeaders = (userId: number | string, authToken: string): Record<string, string> => {
  return {
    'User-Id': userId.toString(),
    'Auth-Token': authToken,
  };
};

// login, register and profile responses carry the user's email, which decides whether the debug panel is on
const checkDebugUser = <T>(response: Promise<T>): Promise<T> =>
  response.then(data => {
    void rememberDebugUser((data as { userEmail?: unknown } | undefined)?.userEmail);
    return data;
  });

export class ApiService extends WebRequestService {
  baseUrl: string;

  constructor() {
    super();
    this.baseUrl = 'api/v1/';
  }

  checkCredentials(site: string, key: string, secret: string, userId: number, authToken: string) {
    return this.postJson('credentials/check', { site, key, secret }, makeHeaders(userId, authToken));
  }

  forgotPassword(
    userId: number,
    token: string,
    goodUntil: number,
    password: string,
    passwordConfirmation: string
  ): Promise<AuthData> {
    return this.postJson('forgot_password', { userId, token, goodUntil, password, passwordConfirmation });
  }

  forgotPasswordRequest(email: string): Promise<void> {
    return this.postJson('request_forgot_password', { email });
  }

  async proxyEthplorer(userId: number, authToken: string, address: string): Promise<AddressInfo> {
    return this.getJson(`proxy_ethplorer/${address}`, {}, makeHeaders(userId, authToken));
  }

  loadSettings(userId: number, authToken: string): Promise<SettingsData> {
    return checkDebugUser(this.getJson('profile', {}, makeHeaders(userId, authToken)));
  }

  updateSettings(data: unknown, userId: number, authToken: string): Promise<SettingsData> {
    return checkDebugUser(this.postJson('profile', data, makeHeaders(userId, authToken)));
  }

  login(email: string, password: string): Promise<AuthData> {
    return checkDebugUser(this.postJson('login', { email, password }));
  }

  register(email: string, password: string, passwordConfirmation: string): Promise<AuthData> {
    return checkDebugUser(this.postJson('register', { email, password, passwordConfirmation }));
  }

  getOrders(userId: number, authToken: string): Promise<Array<Order>> {
    return this.getJson('orders', {}, makeHeaders(userId, authToken));
  }

  getWalletOverTime(): Promise<Array<WalletMapping>> {
    return this.getJson('wallet_over_time');
  }

  getBalances(userId: number | string, authToken: string): Promise<Balance | Array<Balance>> {
    return this.getJson('balances', {}, makeHeaders(userId, authToken));
  }

  extractJsonData(json: { data: Array<unknown> }) {
    return json.data.length === 1 ? head(json.data) : json.data;
  }
}
